import React, { useState, useEffect } from 'react';
import { Plus, ClipboardList, Bell, Calendar, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../services/firebase';
import NoticeForm from '../components/notices/NoticeForm';
import NoticeList from '../components/notices/NoticeList';
import { ConfirmDialog } from '../components/ui';
import { noticesApi } from '../services/api';
import { Notice, NoticeFormData } from '../types';

const Notices: React.FC = () => {
  const [user] = useAuthState(auth);
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingNotice, setEditingNotice] = useState<Notice | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Dialog states
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  useEffect(() => {
    loadNotices();
  }, []);

  const loadNotices = async () => {
    try {
      setLoading(true);
      const noticesData = await noticesApi.getAll();
      setNotices(noticesData);
    } catch (error) {
      toast.error('Failed to load notices');
      console.error('Error loading notices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNotice = async (data: NoticeFormData) => {
    if (!user?.email) {
      toast.error('You must be logged in to create notices');
      return;
    }

    try {
      setSubmitting(true);
      const newNotice = await noticesApi.create(data, user.email);
      setNotices(prev => [newNotice, ...prev]);
      setShowForm(false);
      toast.success('Notice created successfully');
    } catch (error) {
      toast.error('Failed to create notice');
      console.error('Error creating notice:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditNotice = async (data: NoticeFormData) => {
    if (!editingNotice) return;

    try {
      setSubmitting(true);
      await noticesApi.update(editingNotice.id, data);
      setNotices(prev =>
        prev.map(notice =>
          notice.id === editingNotice.id
            ? {
                ...notice,
                ...data,
                attachmentURL: data.attachment ? 'updated' : notice.attachmentURL,
                updatedAt: new Date().toISOString()
              }
            : notice
        )
      );
      setEditingNotice(null);
      setShowForm(false);
      toast.success('Notice updated successfully');
    } catch (error) {
      toast.error('Failed to update notice');
      console.error('Error updating notice:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNotice = (notice: Notice) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Notice',
      message: `Are you sure you want to delete "${notice.title}"?\n\nThis action cannot be undone and will remove the notice permanently.`,
      onConfirm: async () => {
        try {
          await noticesApi.delete(notice.id);
          setNotices(prev => prev.filter(n => n.id !== notice.id));
          toast.success('Notice deleted successfully');
        } catch (error) {
          toast.error('Failed to delete notice');
          console.error('Error deleting notice:', error);
        }
      }
    });
  };

  const handleTogglePublish = async (notice: Notice) => {
    try {
      await noticesApi.update(notice.id, {
        published: !notice.published,
        title: notice.title,
        description: notice.description,
        priority: notice.priority,
        date: notice.date
      });
      setNotices(prev =>
        prev.map(n =>
          n.id === notice.id
            ? { ...n, published: !n.published, updatedAt: new Date().toISOString() }
            : n
        )
      );
      toast.success(`Notice ${notice.published ? 'unpublished' : 'published'} successfully`);
    } catch (error) {
      toast.error(`Failed to ${notice.published ? 'unpublish' : 'publish'} notice`);
      console.error('Error toggling notice publish status:', error);
    }
  };

  const handleViewNotice = (notice: Notice) => {
    // For now, we'll use the edit form in view mode
    setEditingNotice(notice);
    setShowForm(true);
  };

  const handleEdit = (notice: Notice) => {
    setEditingNotice(notice);
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingNotice(null);
  };

  // Calculate real-time stats from notice data
  const realTimeStats = {
    totalNotices: notices.length,
    publishedNotices: notices.filter(n => n.published).length,
    monthlyNotices: notices.filter(n => {
      const noticeDate = new Date(n.createdAt);
      const currentDate = new Date();
      return noticeDate.getMonth() === currentDate.getMonth() &&
             noticeDate.getFullYear() === currentDate.getFullYear();
    }).length,
    urgentNotices: notices.filter(n => n.priority === 'Urgent' && n.published).length
  };

  if (showForm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#f3ede2]">
              {editingNotice ? 'Edit Notice' : 'Create New Notice'}
            </h1>
            <p className="text-[#b9ad9b] mt-1">
              {editingNotice ? 'Update notice details' : 'Create a new announcement for the notice board'}
            </p>
          </div>
        </div>

        <NoticeForm
          onSubmit={editingNotice ? handleEditNotice : handleCreateNotice}
          loading={submitting}
          onCancel={handleCancelForm}
          initialData={editingNotice ? {
            title: editingNotice.title,
            description: editingNotice.description,
            priority: editingNotice.priority,
            date: editingNotice.date,
            published: editingNotice.published
          } : undefined}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Actions */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-[#e2916d] hover:bg-[#f0a67d] text-white rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Create Notice</span>
        </button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-[#211c15] rounded-lg border border-[#3a3122] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#b9ad9b] text-sm font-medium">Total Notices</p>
              <p className="text-2xl font-bold text-[#f3ede2] mt-2">{realTimeStats.totalNotices}</p>
            </div>
            <div className="w-12 h-12 bg-[#16212b] rounded-lg flex items-center justify-center">
              <ClipboardList className="w-6 h-6 text-[#60a5fa]" />
            </div>
          </div>
        </div>

        <div className="bg-[#211c15] rounded-lg border border-[#3a3122] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#b9ad9b] text-sm font-medium">Published</p>
              <p className="text-2xl font-bold text-[#f3ede2] mt-2">{realTimeStats.publishedNotices}</p>
            </div>
            <div className="w-12 h-12 bg-[#173322] rounded-lg flex items-center justify-center">
              <Bell className="w-6 h-6 text-[#4ade80]" />
            </div>
          </div>
        </div>

        <div className="bg-[#211c15] rounded-lg border border-[#3a3122] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#b9ad9b] text-sm font-medium">This Month</p>
              <p className="text-2xl font-bold text-[#f3ede2] mt-2">{realTimeStats.monthlyNotices}</p>
            </div>
            <div className="w-12 h-12 bg-[#332711] rounded-lg flex items-center justify-center">
              <Calendar className="w-6 h-6 text-[#f0b64c]" />
            </div>
          </div>
        </div>

        <div className="bg-[#211c15] rounded-lg border border-[#3a3122] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#b9ad9b] text-sm font-medium">Urgent</p>
              <p className="text-2xl font-bold text-[#f3ede2] mt-2">{realTimeStats.urgentNotices}</p>
            </div>
            <div className="w-12 h-12 bg-[#341a17] rounded-lg flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-[#f38b83]" />
            </div>
          </div>
        </div>
      </div>

      {/* Notice List */}
      <NoticeList
        notices={notices}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDeleteNotice}
        onView={handleViewNotice}
        onTogglePublish={handleTogglePublish}
      />

      {/* Dialog Components */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type="danger"
      />
    </div>
  );
};

export default Notices;